'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Play, RotateCcw, ChevronDown, Loader2, Info } from 'lucide-react';
import type { BacktestStatus } from './BacktestWorkspace';
import type { BacktestConfigValues } from './BacktestWorkspace';

interface BacktestConfigPanelProps {
  status: BacktestStatus;
  progress: number;
  onRun: () => void;
  onReset: () => void;
  onConfigChange: (values: BacktestConfigValues) => void;
}

interface BacktestConfig {
  symbol: string;
  timeframe: string;
  startDate: string;
  endDate: string;
  initialCapital: string;
  riskPerTrade: string;
  stopLossType: string;
  takeProfitType: string;
  minPatternScore: string;
  maxOpenTrades: string;
}

const savedStrategies = [
  { id: 'strat-001', name: 'Breakout above resistance', tags: 2 },
  { id: 'strat-002', name: 'VWAP reclaim + momentum', tags: 3 },
  { id: 'strat-003', name: 'Gap fill + reversal', tags: 1 },
];

const assetCategories = [
  { id: 'stocks', label: 'US Stocks' },
  { id: 'commodities', label: 'Commodities' },
  { id: 'futures', label: 'US Futures' },
];

const symbolsByCategory: Record<string, { value: string; label: string }[]> = {
  stocks: [
    { value: 'NVDA', label: 'NVDA – Nvidia' },
    { value: 'AAPL', label: 'AAPL – Apple' },
    { value: 'TSLA', label: 'TSLA – Tesla' },
    { value: 'SPY', label: 'SPY – S&P 500 ETF' },
    { value: 'QQQ', label: 'QQQ – Nasdaq ETF' },
    { value: 'META', label: 'META – Meta' },
    { value: 'MSFT', label: 'MSFT – Microsoft' },
    { value: 'AMZN', label: 'AMZN – Amazon' },
    { value: 'COIN', label: 'COIN – Coinbase' },
    { value: 'AMD', label: 'AMD – AMD' },
  ],
  commodities: [
    { value: 'GC=F', label: 'GC – Gold' },
    { value: 'SI=F', label: 'SI – Silver' },
    { value: 'CL=F', label: 'CL – Crude Oil (WTI)' },
    { value: 'BZ=F', label: 'BZ – Brent Crude' },
    { value: 'NG=F', label: 'NG – Natural Gas' },
    { value: 'HG=F', label: 'HG – Copper' },
    { value: 'PL=F', label: 'PL – Platinum' },
    { value: 'PA=F', label: 'PA – Palladium' },
    { value: 'ZC=F', label: 'ZC – Corn' },
    { value: 'ZW=F', label: 'ZW – Wheat' },
    { value: 'ZS=F', label: 'ZS – Soybeans' },
    { value: 'CC=F', label: 'CC – Cocoa' },
    { value: 'KC=F', label: 'KC – Coffee' },
    { value: 'CT=F', label: 'CT – Cotton' },
    { value: 'SB=F', label: 'SB – Sugar' },
  ],
  futures: [
    { value: 'ES=F', label: 'ES – S&P 500 Futures' },
    { value: 'NQ=F', label: 'NQ – Nasdaq 100 Futures' },
    { value: 'YM=F', label: 'YM – Dow Jones Futures' },
    { value: 'RTY=F', label: 'RTY – Russell 2000 Futures' },
    { value: 'ZB=F', label: 'ZB – 30Y T-Bond Futures' },
    { value: 'ZN=F', label: 'ZN – 10Y T-Note Futures' },
    { value: 'ZF=F', label: 'ZF – 5Y T-Note Futures' },
    { value: '6E=F', label: '6E – Euro FX Futures' },
    { value: '6J=F', label: '6J – Japanese Yen Futures' },
    { value: '6B=F', label: '6B – British Pound Futures' },
    { value: 'VX=F', label: 'VX – VIX Futures' },
    { value: 'MES=F', label: 'MES – Micro E-mini S&P 500' },
    { value: 'MNQ=F', label: 'MNQ – Micro E-mini Nasdaq' },
  ],
};

export default function BacktestConfigPanel({ status, progress, onRun, onReset, onConfigChange }: BacktestConfigPanelProps) {
  const [savedOpen, setSavedOpen] = useState(false);
  const [assetCategory, setAssetCategory] = useState<'stocks' | 'commodities' | 'futures'>('stocks');
  const { register, handleSubmit, watch, setValue } = useForm<BacktestConfig>({
    defaultValues: {
      symbol: 'NVDA',
      timeframe: '15m',
      startDate: '2025-01-01',
      endDate: '2026-08-01',
      initialCapital: '25000',
      riskPerTrade: '1',
      stopLossType: 'fixed-atr',
      takeProfitType: 'fixed-rr',
      minPatternScore: '70',
      maxOpenTrades: '3',
    },
  });

  const onSubmit = (data: BacktestConfig) => {
    onConfigChange(data);
    onRun();
  };

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden card-glow-primary h-fit sticky top-6">
      {/* Panel header */}
      <div className="px-4 py-3.5 border-b border-border">
        <h3 className="text-sm font-600 text-foreground">Backtest Configuration</h3>
        <p className="text-xs text-muted-foreground mt-0.5">Set parameters and run your strategy</p>
      </div>

      {/* Saved strategies */}
      <div className="px-4 py-3 border-b border-border">
        <button
          onClick={() => setSavedOpen(!savedOpen)}
          className="w-full flex items-center justify-between text-xs font-500 text-muted-foreground hover:text-foreground transition-colors"
        >
          <span>Saved Strategies</span>
          <ChevronDown size={12} className={`transition-transform ${savedOpen ? 'rotate-180' : ''}`} />
        </button>
        {savedOpen && (
          <div className="mt-2 space-y-1.5 animate-slide-up">
            {savedStrategies.map((s) => (
              <button
                key={s.id}
                className="w-full text-left flex items-center justify-between px-3 py-2 rounded-lg bg-muted/50 hover:bg-muted hover:border-primary/20 border border-transparent transition-all text-xs"
              >
                <span className="text-foreground font-500 truncate">{s.name}</span>
                <span className="text-muted-foreground ml-2 flex-shrink-0">{s.tags} drawings</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="px-4 py-4 space-y-4">
        {/* Asset Category Tabs */}
        <div>
          <label className="block text-xs font-500 text-foreground mb-1.5">Asset Class</label>
          <div className="flex rounded-lg bg-muted p-0.5 gap-0.5">
            {assetCategories.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => {
                  setAssetCategory(cat.id as 'stocks' | 'commodities' | 'futures');
                  const firstSymbol = symbolsByCategory[cat.id][0].value;
                  setValue('symbol', firstSymbol);
                }}
                className={`flex-1 py-1.5 text-xs font-500 rounded-md transition-all duration-150 ${
                  assetCategory === cat.id
                    ? 'bg-card text-primary shadow-sm border border-primary/20'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Symbol + Timeframe */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-500 text-foreground mb-1.5">Symbol</label>
            <div className="relative">
              <select
                {...register('symbol')}
                className="w-full bg-input border border-border rounded-lg px-2.5 py-2 text-xs text-foreground outline-none focus:border-primary/60 appearance-none font-mono-nums"
              >
                {symbolsByCategory[assetCategory].map((sym) => (
                  <option key={`cfg-sym-${sym.value}`} value={sym.value}>{sym.label}</option>
                ))}
              </select>
              <ChevronDown size={10} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-500 text-foreground mb-1.5">Timeframe</label>
            <div className="relative">
              <select
                {...register('timeframe')}
                className="w-full bg-input border border-border rounded-lg px-2.5 py-2 text-xs text-foreground outline-none focus:border-primary/60 appearance-none"
              >
                {['1m', '5m', '15m', '30m', '1H', '4H', '1D'].map((tf) => (
                  <option key={`cfg-tf-${tf}`} value={tf}>{tf}</option>
                ))}
              </select>
              <ChevronDown size={10} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Date range */}
        <div>
          <label className="block text-xs font-500 text-foreground mb-1.5">Backtest Period</label>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className="text-xs text-muted-foreground mb-1">From</p>
              <input
                type="date"
                {...register('startDate')}
                className="w-full bg-input border border-border rounded-lg px-2.5 py-2 text-xs text-foreground outline-none focus:border-primary/60"
              />
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">To</p>
              <input
                type="date"
                {...register('endDate')}
                className="w-full bg-input border border-border rounded-lg px-2.5 py-2 text-xs text-foreground outline-none focus:border-primary/60"
              />
            </div>
          </div>
        </div>

        {/* Capital + Risk */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-500 text-foreground mb-1.5">Capital ($)</label>
            <input
              type="number"
              {...register('initialCapital')}
              className="w-full bg-input border border-border rounded-lg px-2.5 py-2 text-xs text-foreground outline-none focus:border-primary/60 font-mono-nums"
            />
          </div>
          <div>
            <label className="block text-xs font-500 text-foreground mb-1.5">
              Risk / Trade
              <span className="text-muted-foreground ml-1">%</span>
            </label>
            <input
              type="number"
              step="0.1"
              {...register('riskPerTrade')}
              className="w-full bg-input border border-border rounded-lg px-2.5 py-2 text-xs text-foreground outline-none focus:border-primary/60 font-mono-nums"
            />
          </div>
        </div>

        {/* Stop / TP type */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-500 text-foreground mb-1.5">Stop Loss</label>
            <div className="relative">
              <select
                {...register('stopLossType')}
                className="w-full bg-input border border-border rounded-lg px-2.5 py-2 text-xs text-foreground outline-none focus:border-primary/60 appearance-none"
              >
                <option value="fixed-atr">Fixed ATR</option>
                <option value="pattern-low">Pattern Low</option>
                <option value="fixed-pct">Fixed %</option>
                <option value="sr-level">S/R Level</option>
              </select>
              <ChevronDown size={10} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-500 text-foreground mb-1.5">Take Profit</label>
            <div className="relative">
              <select
                {...register('takeProfitType')}
                className="w-full bg-input border border-border rounded-lg px-2.5 py-2 text-xs text-foreground outline-none focus:border-primary/60 appearance-none"
              >
                <option value="fixed-rr">Fixed R:R</option>
                <option value="pattern-high">Pattern High</option>
                <option value="fixed-pct">Fixed %</option>
                <option value="sr-level">S/R Level</option>
              </select>
              <ChevronDown size={10} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Min pattern score */}
        <div>
          <div className="flex items-center gap-1.5 mb-1.5">
            <label className="text-xs font-500 text-foreground">Min Pattern Match Score</label>
            <Info size={11} className="text-muted-foreground" title="Only trade setups where the pattern similarity score exceeds this threshold" />
          </div>
          <p className="text-xs text-muted-foreground mb-2">
            Only execute trades where pattern similarity ≥ this %
          </p>
          <div className="flex items-center gap-3">
            <input
              type="range"
              min="50"
              max="95"
              step="5"
              {...register('minPatternScore')}
              className="flex-1 accent-primary"
            />
            <span className="text-xs font-mono-nums text-primary font-600 w-8 text-right">70%</span>
          </div>
        </div>

        {/* Max open trades */}
        <div>
          <label className="block text-xs font-500 text-foreground mb-1.5">Max Concurrent Trades</label>
          <input
            type="number"
            min="1"
            max="10"
            {...register('maxOpenTrades')}
            className="w-full bg-input border border-border rounded-lg px-2.5 py-2 text-xs text-foreground outline-none focus:border-primary/60 font-mono-nums"
          />
        </div>

        {/* Progress bar */}
        {status === 'running' && (
          <div className="animate-fade-in">
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-xs text-muted-foreground">Running backtest…</p>
              <p className="text-xs font-mono-nums text-primary">{progress}%</p>
            </div>
            <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary rounded-full progress-bar-inner"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1.5">
              Scanning {Math.round(progress * 4.7)} / 470 bars…
            </p>
          </div>
        )}

        {/* Action buttons */}
        <div className="space-y-2 pt-1">
          {status !== 'complete' ? (
            <button
              type="submit"
              disabled={status === 'running'}
              className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-xl py-2.5 text-sm font-600 hover:bg-primary/90 active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-150"
            >
              {status === 'running' ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Running Backtest…
                </>
              ) : (
                <>
                  <Play size={14} />
                  Run Backtest
                </>
              )}
            </button>
          ) : (
            <button
              type="button"
              onClick={onReset}
              className="w-full flex items-center justify-center gap-2 bg-muted border border-border text-foreground rounded-xl py-2.5 text-sm font-500 hover:border-primary/40 active:scale-[0.99] transition-all duration-150"
            >
              <RotateCcw size={14} />
              New Backtest
            </button>
          )}

          {status === 'idle' && (
            <button
              type="button"
              className="w-full flex items-center justify-center gap-2 bg-transparent border border-border text-muted-foreground rounded-xl py-2 text-xs font-500 hover:border-primary/30 hover:text-foreground transition-all duration-150"
            >
              Save Strategy Pattern
            </button>
          )}
        </div>
      </form>
    </div>
  );
}